import type { Dict } from '../types'

/**
 * French. Vocabulary and tone follow MOS MOSH's own French trade-in copy (Core
 * `brandLocalization.tradein2`): the formal "vous" throughout, reprise for the trade-in, bon d’achat
 * for the reward, articles for the items, point de dépôt, étiquette d’expédition. Typographic
 * apostrophes and the French space before : ! ? are used as in that copy. Translated best-effort —
 * please proofread.
 */
export const fr: Dict = {
  languageName: 'Français',

  common: {
    points: 'points',
  },

  // Subject-word lexicon (see en.ts). French defaults for clothing (vêtements = masculine plural).
  // A brand supplies the full phrase per form, so its own gender and number travel with the word.
  //
  // bon d’achat is masculine. French adjective agreement makes a handful of strings gender-bound, so
  // this dictionary prefers invariant wording ("disponible", "valable") wherever the reward word is
  // the subject; the two places where a masculine article is unavoidable are flagged below.
  subject: {
    noun: 'vêtements',
    nounPoss: 'vos vêtements',
    nounDef: 'les vêtements',
    reward: 'bon d’achat',
    rewardPoss: 'votre bon d’achat',
    rewardDef: 'le bon d’achat',
    rewardPlural: 'bons d’achat',
  },

  auth: {
    signingIn: 'Connexion en cours…',
    stillWorking: 'Toujours en cours — nous récupérons vos commandes précédentes. Cela peut prendre un instant.',
    loadingItems: 'Chargement de vos articles…',
    errorTitle: 'Nous n’avons pas pu démarrer votre reprise',
    errorGeneric: 'Un problème est survenu lors de la connexion.',
    missingShop: 'L’application est mal configurée — shopId manquant.',
    needSignIn: 'Vous devez vous connecter avant de faire une reprise.',
    signInFailed: 'La connexion a échoué. Veuillez réessayer.',
    linkInvalid: 'Ce lien de connexion ne fonctionne plus. Le profil auquel il appartient a peut-être été supprimé. Revenez à la page d’accueil pour vous reconnecter.',
    backToFrontPage: 'Retour à la page d’accueil',
  },

  config: {
    title: 'Erreur de configuration',
    missingShop: 'Cette reprise n’a pas sa configuration boutique (shopId). Veuillez contacter la boutique.',
  },

  login: {
    title: 'Offrez une seconde vie à {nounPoss}',
    subtitle:
      'Connectez-vous pour démarrer votre reprise et transformer les pièces que vous ne portez plus en {reward}.',
    emailLabel: 'E-mail',
    emailPlaceholder: 'vous@exemple.fr',
    start: 'Continuer',
    working: 'Veuillez patienter…',
    emailSentTitle: 'Consultez votre e-mail',
    emailSentBody: 'Nous avons envoyé un lien de connexion à {email}. Ouvrez-le sur cet appareil pour continuer.',
    phoneLabel: 'Numéro de téléphone',
    phonePlaceholder: '06 12 34 56 78',
    sendCode: 'Envoyer le code',
    codeLabel: 'Code de vérification',
    codePlaceholder: 'Code à 6 chiffres',
    verify: 'Vérifier et continuer',
    changePhone: 'Utiliser un autre numéro',
    back: 'Retour',
    methodSms: 'Se connecter par SMS',
    methodSmsDesc: 'Nous vous enverrons un code par SMS — si votre numéro est enregistré chez nous.',
    methodEmail: 'Se connecter par e-mail',
    methodEmailDesc: 'Nous enverrons un lien de connexion dans votre boîte de réception.',
    or: 'ou',
    wholesale: 'Acheté en magasin ou reçu en cadeau ?',
    emailInvalid: 'Saisissez une adresse e-mail valide.',
    phoneInvalid: 'Saisissez un numéro de téléphone valide.',
    codeInvalid: 'Ce code n’a pas fonctionné. Réessayez.',
    failed: 'Un problème est survenu. Veuillez réessayer.',
    step1Title: 'Trouvez vos pièces',
    step1Body: 'Recherchez ou prenez en photo ce que vous ne portez plus.',
    step2Title: 'Envoyez-les',
    step2Body: 'Emballez le tout dans un seul colis avec votre étiquette prépayée.',
    step3Title: 'Recevez {rewardPoss}',
    step3Body: {
      received: 'Recevez {rewardPoss} dès que nous avons reçu vos articles.',
      handin: 'Recevez {rewardPoss} dès que votre colis est scanné au point de dépôt.',
      instant: 'Recevez {rewardPoss} immédiatement — puis envoyez-nous vos articles.',
    },
  },

  country: {
    title: 'Votre pays n’est peut-être pas pris en charge',
    body: 'Il semble que nous n’ayons pas de transporteur capable d’acheminer une reprise depuis votre pays jusqu’à notre entrepôt. Vous pouvez continuer, mais nous ne pourrons peut-être pas finaliser la collecte.',
    dismiss: 'Continuer quand même',
  },

  menu: {
    open: 'Menu',
    language: 'Langue',
    myTradeIns: 'Mes reprises',
    howItWorks: 'Comment ça marche',
    noneYet: 'Aucune pour l’instant',
    logout: 'Se déconnecter',
    soon: 'Bientôt',
  },

  // Lignes de bons d’achat, réutilisées par la page « Mes reprises » pour la section des bons.
  rewards: {
    activeHeading: 'Disponibles',
    spentHeading: 'Utilisés',
    expires: 'Expire le {date}',
    active: 'Actif',
    spentBadge: 'Utilisé',
  },
  // Page de compte « Mes reprises » : les bons d’achat d’abord, puis l’historique + la fiche détail.
  myTradeIns: {
    title: 'Mes reprises',
    subtitle: 'Vos reprises et vos {rewardPlural}.',
    back: 'Retour à la reprise',
    loading: 'Chargement de vos reprises…',
    empty: 'Vous n’avez encore aucune reprise ni {rewardPlural}.',
    historyHeading: 'Vos reprises',
    tradeInPrefix: 'Reprise',
    itemsCount: '{count} articles',
    statusReceived: 'Reçu',
    statusOnItsWay: 'En route vers nous',
    statusAwaiting: 'En attente de votre colis',
    detailTitle: 'Détails de la reprise',
    nextHeading: 'Ce qui se passe maintenant',
    nextReceived: 'Nous avons reçu vos articles — {rewardPoss} est en route.',
    nextTransit:
      'Votre colis est en route vers nous. Nous vous enverrons un e-mail dès que nous aurons reçu et vérifié vos articles.',
    nextPickup: 'Apportez vos articles dans notre magasin et nous nous occupons du reste.',
    nextShip: 'Emballez vos articles et envoyez-les-nous à l’aide des informations ci-dessous.',
    itemsHeading: 'Articles de cette reprise',
    labelUnavailable: 'L’étiquette d’expédition n’est plus disponible.',
    close: 'Fermer',
  },

  steps: {
    items: 'Articles',
    story: 'Votre histoire',
    details: 'Informations',
    shipping: 'Expédition',
    confirm: 'Confirmer',
  },

  rail: {
    title: 'Votre reprise',
    itemCount: '{count} article',
    itemCountPlural: '{count} articles',
    empty: 'Aucun article pour l’instant — ajoutez votre première pièce et voyez la valeur grimper.',
    loading: 'Chargement de votre reprise…',
    estimatedReward: 'Valeur totale',
    // Ces trois lignes qualifient « la valeur » (féminin), pas le mot de la marque : l’accord ne
    // dépend donc pas du terme choisi par la marque.
    rewardNoteConfirmed: {
      received: 'Confirmée à la réception de vos articles',
      handin: 'Confirmée au dépôt de votre colis',
      instant: 'Émise dès que vous confirmez votre reprise',
    },
    rewardNoteAdd: 'Ajoutez des articles pour voir la valeur',
    addedManually: 'Ajouté manuellement',
    maxExceeded: 'Cette reprise dépasse le maximum de {amount}. Retirez un article pour continuer.',
    remove: 'Retirer {name}',
    back: 'Retour',
    continue: 'Continuer',
    confirmTradeIn: 'Confirmer la reprise',
    done: 'Terminé',
  },

  items: {
    firstTitle: 'Trouvons votre premier article',
    firstSubtitle: 'Prenez une photo ou cherchez dans le catalogue.',
    anotherTitle: 'Ajouter un autre article ?',
    anotherSubtitle: 'Vérifiez le tout avant de continuer.',
    selectTitle: 'Sélectionnez les articles à faire reprendre',
    selectSubtitle: 'Parmi vos commandes précédentes — touchez pour ajouter.',
    manualTitle: 'Ajouter un article manuellement',
    manualSubtitle: 'Pour les articles achetés ailleurs.',
    estReward: 'Valeur',
    bought: 'Acheté le {date}',
    addManually: 'Ajouter manuellement',
    boughtElsewhere: 'Acheté ailleurs ?',
    addedCount: '{count} ajoutés',
    nothingTitle: 'Rien à faire reprendre',
    nothingBody:
      'Nous n’avons trouvé sur votre compte aucun article éligible à la reprise. La reprise ici ne concerne que les articles achetés chez nous — dès que vous aurez une commande éligible, elle apparaîtra ici.',
  },

  intro: {
    title: 'Comment fonctionne la reprise',
    lead: 'Faites reprendre {nounPoss} {brand} que vous ne portez plus — et recevez {rewardPoss} à dépenser sur des pièces neuves ou secondhand.',
    step1Title: 'Trouvez vos pièces',
    step1Body:
      'Prenez en photo ce que vous ne portez plus, ou cherchez dans notre catalogue. Ajoutez autant de pièces que vous le souhaitez.',
    step2Title: 'Envoyez-les',
    step2Body: 'Emballez le tout dans un seul colis avec votre étiquette prépayée — l’expédition est offerte.',
    step3Title: 'Recevez {rewardPoss}',
    step3Body: {
      received: 'Dès que nous aurons reçu et vérifié vos articles, nous vous enverrons {rewardPoss} par e-mail.',
      handin: 'Dès que votre colis est scanné au dépôt, nous vous envoyons {rewardPoss} par e-mail.',
      instant: '{rewardPoss|cap} vous est envoyé par e-mail dès que vous confirmez — ensuite, envoyez-nous vos articles.',
    },
    cta: 'J’ai compris',
    close: 'Fermer',
  },

  finder: {
    search: 'Chercher dans le catalogue',
    photo: 'Ajouter par photo',
    searchTitle: 'Chercher dans le catalogue',
    searchSub: 'Par SKU, EAN ou nom de produit.',
    searchPlaceholder: 'Rechercher par SKU, EAN ou nom de produit',
    carelabelHint: 'Astuce : le SKU ou le numéro de style figure sur l’étiquette d’entretien de votre article.',
    searching: 'Recherche en cours…',
    searchFailed: 'La recherche a échoué — réessayez.',
    noMatches: 'Aucun résultat pour « {query} ».',
    unavailable: 'La recherche dans le catalogue n’est pas disponible pour cette boutique.',
    estReward: 'Valeur',
    notEligible: 'Non éligible',
    add: 'Ajouter',
    adding: 'Ajout en cours…',
    added: '{name} ajouté · +{amount}',
    backToOrders: 'Retour à vos commandes',
    showMore: 'Afficher plus de résultats',
    loadingMore: 'Chargement…',
  },

  photo: {
    dropTitle: 'Prenez votre article en photo',
    dropHint: 'Nous chercherons des articles similaires dans notre catalogue.',
    or: 'ou',
    wayDevice: 'Prendre ou importer une photo',
    wayPhone: 'Utiliser l’appareil photo de votre téléphone',
    qrAgainTitle: 'Essayer une autre photo',
    qrAgainHint: 'Prenez une nouvelle photo avec votre téléphone et nous relancerons la recherche automatiquement.',
    analysingTitle: 'Recherche des meilleures correspondances…',
    analysingHint: 'Comparaison de votre photo avec notre catalogue',
    uploadingTitle: 'Envoi de votre photo…',
    uploadingHint: 'Un instant',
    yourPhoto: 'Votre photo',
    matchedTo: 'Associé à notre catalogue',
    replace: 'Remplacer',
    headingConfident: 'L’un de ces articles ressemble-t-il au vôtre ?',
    headingCategory: 'Correspondances les plus proches dans {category}',
    noMatchHeading: 'Nous n’avons pas trouvé de correspondance proche',
    noMatchBody:
      'Aucun article de notre catalogue ne correspond de près. Essayez une autre photo, ou effectuez plutôt une recherche par nom.',
    // Distinct from "no match": the search never ran, so the item may well be in the catalogue.
    notSearched: 'Pas encore recherché',
    searchFailedHeading: 'Nous n’avons pas pu lancer la recherche',
    searchFailedBody:
      'Un problème est survenu de notre côté, votre photo n’a donc pas encore été comparée. Votre photo est enregistrée — veuillez réessayer.',
    tryAgain: 'Réessayer',
    refine: 'Affiner par catégorie',
    clear: 'Effacer',
    noneOfThese: 'Aucun de ceux-ci',
    // « Ajouter par catégorie », accessible depuis la vignette de réserve dans les résultats photo.
    categoryCardTitle: 'Vous ne trouvez pas votre article ?',
    categoryCardHint: 'Choisissez plutôt une catégorie',
    categoryPickTitle: 'De quel type d’article s’agit-il ?',
    categoryPickHint: 'Nous utiliserons la photo que vous venez de prendre.',
    categoryValue: 'Valeur',
    categoryConfirm: 'Ajouter à la reprise',
    categoryBack: 'Retour',
    uploadAnother: 'Importer une autre photo',
    bestMatch: 'Meilleure correspondance',
    uploadFailed: 'Un problème est survenu — veuillez réessayer.',
  },

  match: {
    strong: 'Forte correspondance',
    colour: 'Couleur : {color}',
    estimatedReward: 'Valeur',
    backToMatches: 'Retour aux correspondances',
    backToResults: 'Retour aux résultats',
    confirm: 'Oui, c’est mon article',
  },

  details: {
    title: 'Vos informations',
    subtitle: 'Pour que nous puissions créer votre étiquette d’expédition prépayée et vous envoyer {rewardPoss}.',
    contact: 'Contact',
    address: 'Adresse',
    fullName: 'Nom complet',
    email: 'E-mail',
    country: 'Pays',
    streetAddress: 'Adresse postale',
    postalCode: 'Code postal',
    city: 'Ville',
    prefix: 'Indicatif du pays',
    phone: 'Téléphone',
    emailLocked: 'Votre adresse e-mail est liée à votre compte et ne peut pas être modifiée ici.',
    consentPre: 'J’accepte les ',
    consentLink: 'conditions générales de reprise',
    consentPost: ' et je confirme que les articles m’appartiennent.',
    required: 'Ce champ est obligatoire.',
    emailInvalid: 'Saisissez une adresse e-mail valide.',
    phoneInvalid: 'Saisissez un numéro de téléphone valide.',
    zipInvalid: 'Saisissez un code postal valide',
    zipFormat: 'format : {format}',
    loading: 'Chargement de vos informations…',
    saveFailed: 'Nous n’avons pas pu enregistrer vos informations. Veuillez réessayer.',
  },

  terms: {
    title: 'Conditions générales de reprise de {noun}',
    // {brand} est le nom de marque de la boutique (config.brandName), ou une valeur générique.
    introLead:
      'Dans le cadre de notre initiative secondhand, vous pouvez faire reprendre {nounPoss} {brand} d’occasion contre un {reward}.',
    introAccept:
      'En nous envoyant {nounPoss} {brand} d’occasion, vous acceptez qu’un {reward} ne soit émis que pour {nounDef} répondant aux conditions ci-dessous, et que {nounDef} ne répondant pas aux exigences ne vous soient PAS renvoyés.',
    introDonate:
      'Les {noun} qui ne répondent pas à nos exigences sont donnés à des associations caritatives ou remis à des entreprises capables de réutiliser les articles ou leurs matières.',
    requirementsTitle: 'Exigences concernant {nounDef} que vous déposez',
    requirementsBody:
      'Nous acceptons {nounDef} {brand} propres et dont toutes les étiquettes d’origine sont intactes — aussi bien l’étiquette de col que l’étiquette d’entretien (instructions de lavage, etc.).',
    howToTitle: 'Comment déposer {nounPoss}',
    howToRegister:
      'Vous enregistrez les produits {brand} que vous souhaitez déposer. Lors de l’enregistrement, vous voyez la valeur de chaque produit — le montant que vous recevrez sous forme de {reward} en échange de {nounPoss}.',
    howToAdjust:
      'Si ce que nous recevons ne correspond pas à ce que vous avez enregistré, nous corrigeons vers le bon produit et ajustons votre valeur totale en conséquence. En nous envoyant {nounPoss}, vous acceptez que nous procédions à cet ajustement.',
    howToLabel:
      'Une fois votre enregistrement terminé, vous recevez une étiquette d’expédition ou un QR code pour nous envoyer {nounPoss}. Cette étiquette d’expédition est prépayée par {brand}.',
    howToFinal:
      'Une fois {nounPoss} déposés, vous ne pouvez plus annuler votre envoi, et nous ne renvoyons pas vos produits.',
    voucherTitle: '{reward|cap}',
    // Ces trois variantes portent l’article indéfini « un », accordé au masculin de « bon d’achat ».
    // Une marque dont le terme est féminin (« une carte cadeau ») doit les remplacer via
    // tradeInConfig.dictionary.fr.
    voucherIssue: {
      received:
        'Vous recevez un {reward} une fois que nous avons confirmé que {nounPoss} répondent aux exigences ci-dessus. {rewardDef|cap} est envoyé à l’adresse e-mail que vous avez indiquée.',
      handin:
        'Vous recevez un {reward} dès que votre colis a été enregistré au point de dépôt. {rewardDef|cap} est envoyé à l’adresse e-mail que vous avez indiquée. Si {nounPoss} ne répondent pas aux exigences ci-dessus, {rewardDef} peut être retiré.',
      instant:
        'Vous recevez un {reward} dès que vous enregistrez votre reprise. {rewardDef|cap} est envoyé à l’adresse e-mail que vous avez indiquée. Si {nounPoss} ne nous sont pas envoyés, ou ne répondent pas aux exigences ci-dessus, {rewardDef} peut être retiré.',
    },
    voucherValidity:
      '{rewardDef|cap} est valable 3 ans à compter de son émission et peut être utilisé pour des achats en ligne chez {brand}. {rewardDef|cap} n’est pas échangeable contre des espèces.',
    closing: 'Nous avons hâte de recevoir {nounPoss}.',
    brandFallback: 'la marque',
    close: 'Fermer',
    accept: 'Accepter et continuer',
  },

  shipping: {
    title: 'Comment souhaitez-vous l’envoyer ?',
    subtitle: 'Emballez votre {count} article — une seule étiquette pour tout.',
    subtitlePlural: 'Emballez vos {count} articles — une seule étiquette pour tout.',
    loading: 'Chargement des options d’expédition…',
    none: 'Aucun service d’expédition n’est encore disponible pour votre adresse.',
    maxExceeded: 'Cette reprise dépasse la valeur maximale. Retirez un article et réessayez.',
    createFailed: 'Nous n’avons pas pu créer votre reprise. Veuillez réessayer.',
    desc_gls: 'Déposez dans un point relais GLS. Gratuit, étiquette imprimée.',
    desc_dhl: 'Déposez dans un point relais DHL. Gratuit, étiquette imprimée.',
    desc_dao: 'Un coursier vient récupérer le colis chez vous.',
    desc_shipmondo: 'Déposez avec une étiquette imprimée.',
    desc_pickup: 'Déposez dans notre magasin physique.',
    desc_postnord: 'Déposez dans n’importe quel point relais PostNord.',
  },

  story: {
    title: 'Racontez-nous votre histoire',
    subtitle: 'Facultatif — nous adorons entendre parler de vos pièces.',
    placeholder: 'Parlez-nous de cette pièce — où vous l’avez portée, pourquoi vous l’aimiez…',
    consent:
      'Vous pouvez partager mon histoire (anonymement) pour inspirer d’autres client·es à offrir une seconde vie à leurs {noun}.',
  },

  confirm: {
    topbarTitle: 'Confirmer',
    heroTitle: 'Tout est prêt',
    heroBody: {
      received: 'Votre reprise est enregistrée. Suivez les trois étapes ci-dessous et {rewardPoss} sera en route.',
      handin:
        'Votre reprise est enregistrée. Suivez les trois étapes ci-dessous — {rewardPoss} est émis dès que vous déposez votre colis.',
      instant:
        'Votre reprise est enregistrée et {rewardPoss} est en route. Il vous suffit de suivre les trois étapes ci-dessous pour nous envoyer vos articles.',
    },
    packTitle: 'Emballez votre {count} article',
    packTitlePlural: 'Emballez vos {count} articles',
    packBody: 'Mettez tout dans un seul colis. Aucun emballage spécial n’est nécessaire — réutilisez un carton ou un sac.',
    pickupPackBody:
      'Emballez vos articles et apportez-les dans notre magasin physique — aucune étiquette d’expédition n’est nécessaire.',
    shippingCode: 'Code d’expédition',
    generatingLabel: 'Création de votre étiquette d’expédition…',
    labelFailed: 'Nous n’avons pas pu générer votre étiquette. Veuillez contacter le service client.',
    showQr: 'Afficher le QR code',
    showLabel: 'Afficher l’étiquette d’expédition',
    dropTitle: 'Déposez-le',
    dropBody: 'Présentez le code ou l’étiquette au point de dépôt de votre transporteur. ',
    findShop: 'Trouver le point de dépôt le plus proche →',
    pickupTitle: 'Déposez en magasin',
    pickupBody: 'Apportez vos articles dans notre magasin, et nous nous occupons du reste.',
    rewardTitle: 'Recevez {rewardPoss}',
    // Coupé autour du montant, que ConfirmPage affiche en <b> entre les deux moitiés. Contrairement
    // au danois, le nom précède le montant (« votre {reward} de [50 €] ») : en français le montant
    // qualifie le bon, il ne s'intercale pas devant lui. « votre » est invariable en genre, donc ces
    // trois lignes fonctionnent quel que soit le terme choisi par la marque.
    rewardBodyPre: {
      received: 'Dès réception de votre colis, nous vous enverrons par e-mail votre {reward} de ',
      handin: 'Dès que votre colis est scanné au dépôt, nous vous enverrons par e-mail votre {reward} de ',
      instant: 'Votre {reward} de ',
    },
    rewardBodyPost: {
      received: ' sous 2 jours ouvrés.',
      handin: '. Comptez quelques heures pour que le suivi du dépôt nous parvienne.',
      instant: ' est déjà en route vers votre boîte de réception — envoyez-nous simplement vos articles en suivant les étapes ci-dessus.',
    },
    // Stratégie INSTANT : le code du bon est affiché ici dès que Core l’a émis. « disponible » et
    // « prêt à l’emploi » sont évités au profit d’une tournure invariable en genre.
    voucherReadyTitle: '{rewardPoss|cap} est disponible',
    voucherIssuing: 'Émission de {rewardPoss}…',
    voucherExpires: 'expire le {date}',
    // Le français ne compose pas comme le danois ({reward}kode) et « le code du {reward} » imposerait
    // un article masculin. Le libellé reste donc générique : il s’affiche sur la carte du bon.
    copyCode: 'Copier le code',
    codeCopied: 'Code copié',
    startAnother: 'Démarrer une nouvelle reprise',
    backToShop: 'Retour à la boutique',
  },

  dropoff: {
    title: 'Points de dépôt les plus proches',
    qrSupported: 'Accepte le dépôt par QR code — aucune étiquette imprimée nécessaire.',
    qrNotSupported: 'Nécessite une étiquette imprimée.',
  },
}
